import { CheckoutEventNames, initializePaddle } from '@paddle/paddle-js';
import type { CheckoutSettings } from '@lyrmo/shared';

/** Where Paddle's checkout links put the transaction to pay: `/?_ptxn=txn_…`. */
export const TRANSACTION_PARAM = '_ptxn';

export type PaddleSettings = NonNullable<CheckoutSettings['paddle']>;

export interface PaddleCheckoutOptions {
  /** Where to go once the payment goes through. */
  readonly successUrl: string;
  /** The checkout was closed without paying. */
  readonly onClosed: () => void;
}

/**
 * Opens Paddle's checkout for the transaction in the address. Paddle.js, which comes from
 * Paddle's CDN, finds `_ptxn` there itself and opens the overlay.
 */
export async function openPaddleCheckout(
  { environment, clientToken }: PaddleSettings,
  { successUrl, onClosed }: PaddleCheckoutOptions,
): Promise<void> {
  let paid = false;
  const paddle = await initializePaddle({
    environment,
    token: clientToken,
    checkout: { settings: { displayMode: 'overlay', successUrl } },
    eventCallback: ({ name }) => {
      if (name === CheckoutEventNames.CHECKOUT_COMPLETED) paid = true;
      else if (name === CheckoutEventNames.CHECKOUT_CLOSED && !paid) onClosed();
    },
  });
  if (!paddle) throw new Error('Paddle.js didn’t load');
}

/** The address without the transaction, so going back or reloading doesn't reopen checkout. */
export function withoutTransaction(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(TRANSACTION_PARAM);
  return url.href;
}
